import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getActiveRole } from "../../../utils/auth";
import { listLevels } from "../../../api/levels";
import { formatLevelLabel } from "../../../utils/formatLevelLabel";
import { listBatches } from "../../../api/batches";
import {
    fetchSessionAttendanceMatrixReport,
    exportSessionAttendanceReportPdf,
    emailSessionAttendanceReport,
} from "../../../api/sessionAttendanceReports";
import DatePicker from "../../../components/common/DatePicker";
import UserLink from "../../../components/common/UserLink";
import Button from "../../../components/common/Button";
import ReportExportBar from "../../../components/reports/ReportExportBar";
import ReportGenerateButton from "../../../components/reports/ReportGenerateButton";
import SessionDateTimeDisplay from "../../../components/common/SessionDateTimeDisplay";
import ExcelDownloadButton from "../../../components/common/ExcelDownloadButton";
import { ListLoadingSpinner } from "../../../components/layout/ListPageLayout";
import { exportMatrixSheet } from "../../../utils/excelExport";
import { formatCurrency } from "../../../utils/currency";
import { SelectInput } from "../../../components/common/form";
import PaginationFooter from "../../../components/common/PaginationFooter";
import useClientPagination from "../../../hooks/useClientPagination";
import {
    regionalDateDaysAgo,
    todayRegionalDate,
    toISODateFromRegional,
} from "../../../utils/date";
import { useReportSettings } from "../../../context/ReportSettingsContext";

const SessionAttendanceReport = () => {
    const { t } = useTranslation();
    const role = getActiveRole() || "ADMIN";
    const { settings: reportSettings } = useReportSettings();
    const showCharges = role === "ADMIN" || reportSettings.coachBatchAttendanceShowCharges === true;

    const getDefaultDates = () => ({
        from: regionalDateDaysAgo(30),
        to: todayRegionalDate(),
    });

    const defaultDates = getDefaultDates();

    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [levelId, setLevelId] = useState("");
    const [batchId, setBatchId] = useState("");

    const [levels, setLevels] = useState([]);
    const [batches, setBatches] = useState([]);

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");

    const [email, setEmail] = useState("");
    const [sendingEmail, setSendingEmail] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [filtersLoadError, setFiltersLoadError] = useState(false);
    const [batchesLoadError, setBatchesLoadError] = useState(false);

    const toIsoDate = (d) => toISODateFromRegional(d) || "";

    // ---------------- Load Levels ----------------
    useEffect(() => {
        listLevels(role)
            .then((res) => {
                setLevels(res.data?.content || []);
                setFiltersLoadError(false);
            })
            .catch(() => {
                setLevels([]);
                setFiltersLoadError(true);
            });
    }, [role]);

    // ---------------- Load Batches (by Level) ----------------
    useEffect(() => {
        let alive = true;

        setBatches([]);
        setBatchId("");

        if (!levelId) {
            return () => { alive = false; };
        }

        setBatchesLoadError(false);
        listBatches(role, { page: 0, size: 500, levelId: Number(levelId) })
            .then((res) => {
                if (!alive) return;
                const vals = res?.data?.content ?? res?.data ?? [];
                setBatches(vals);
                setBatchesLoadError(false);
            })
            .catch(() => {
                if (alive) {
                    setBatches([]);
                    setBatchesLoadError(true);
                }
            });

        return () => { alive = false; };
    }, [role, levelId]);

    // ---------------- Fetch Report ----------------
    const loadReport = async () => {
        if (!fromDate || !toDate || !batchId) {
            alert(t('reports.common.selectFromToBatch'));
            return;
        }

        setLoading(true);
        setReport(null);
        setLoadError("");
        try {
            const res = await fetchSessionAttendanceMatrixReport({
                role,
                fromDate: toIsoDate(fromDate),
                toDate: toIsoDate(toDate),
                batchId: Number(batchId),
            });
            setReport(res.data);
        } catch (e) {
            console.error(e);
            setReport(null);
            setLoadError(t('reports.common.failedToLoad'));
        } finally {
            setLoading(false);
        }
    };

    // ---------------- Export PDF (AUTO DOWNLOAD) ----------------
    const onExportPdf = async () => {
        if (!report) return;

        setExportingPdf(true);
        try {
            const res = await exportSessionAttendanceReportPdf({
                role,
                fromDate: toIsoDate(fromDate),
                toDate: toIsoDate(toDate),
                batchId,
            });

            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "session-attendance-report.pdf";
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            alert(t('reports.common.exportPdfFailed'));
        } finally {
            setExportingPdf(false);
        }
    };

    // ---------------- Send Email ----------------
    const onSendEmail = async () => {
        if (!email) {
            alert(t('reports.common.enterEmailAlert'));
            return;
        }

        setSendingEmail(true);
        try {
            await emailSessionAttendanceReport({
                role,
                email,
                fromDate: toIsoDate(fromDate),
                toDate: toIsoDate(toDate),
                batchId,
            });
            alert(t('reports.common.emailSent'));
            setEmail("");
        } catch (e) {
            console.error(e);
            alert(t('reports.common.emailFailed'));
        } finally {
            setSendingEmail(false);
        }
    };

    const students = report?.students || {};
    const sessions = report?.sessions || [];
    const sessionsPagination = useClientPagination(
        sessions,
        `${batchId}-${fromDate}-${toDate}-${sessions.length}`,
    );

    // ---------------- SORT STUDENTS (A–Z) ----------------
    const sortedStudents = useMemo(() => {
        return Object.entries(students)
            .map(([id, data]) => ({
                id,
                name: data.name,
                openingBalance: data.openingBalance,
                creditsDuringPeriod: data.creditsDuringPeriod,
                totalCharges: data.totalCharges,
                otherBatchCharges: data.otherBatchCharges,
                closingBalance: data.closingBalance,
            }))
            .sort((a, b) =>
                a.name.localeCompare(b.name, "en", { sensitivity: "base" })
            );
    }, [students]);

    return (
        <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600">
                {t('reports.sessionAttendance.subtitle')}
                {showCharges ? t('reports.sessionAttendance.subtitleCharges') : ''}
                {t('reports.sessionAttendance.subtitleEnd')}
            </p>

            {/* Filters */}
            {(filtersLoadError || batchesLoadError) && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">{t('common.optionsLoadFailed')}</div>
            )}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {t('reports.common.filters')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            📅 {t('reports.common.fromDate')}
                        </label>
                        <DatePicker
                            value={fromDate}
                            onChange={setFromDate}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            📅 {t('reports.common.toDate')}
                        </label>
                        <DatePicker
                            value={toDate}
                            onChange={setToDate}
                            className="w-full"
                        />
                    </div>

                    <SelectInput
                        label={t('reports.common.filterByLevel')}
                        value={levelId}
                        onChange={(e) => setLevelId(e.target.value)}
                    >
                        <option value="">{t('reports.common.selectLevel')}</option>
                        {levels.map((l) => (
                            <option key={l.levelId} value={l.levelId}>
                                {formatLevelLabel(l)}
                            </option>
                        ))}
                    </SelectInput>

                    <SelectInput
                        label={t('reports.common.filterByBatch')}
                        value={batchId}
                        onChange={(e) => setBatchId(e.target.value)}
                        disabled={!levelId}
                    >
                        <option value="">{t('reports.common.selectBatch')}</option>
                        {batches.map((b) => {
                            const id = b.batchId ?? b.id;
                            return (
                            <option key={id} value={id}>
                                {b.name}
                            </option>
                            );
                        })}
                    </SelectInput>

                    <div className="flex items-end">
                        <ReportGenerateButton
                            onClick={loadReport}
                            loading={loading}
                            label={t('reports.common.search')}
                            icon="search"
                            className="w-full"
                        />
                    </div>
                    <div className="flex items-end">
                        <ExcelDownloadButton
                            disabled={!report}
                            label={t('common.downloadExcel')}
                            onExport={() => {
                                const studentNames = sortedStudents.map(s => s.name);
                                const headerRow = [t('reports.common.dateTime'), ...studentNames];
                                const dataRows = sessions.map(sess => {
                                    const dt = `${sess.sessionDate || ''} ${sess.sessionTime || sess.startTime || ''}`.trim();
                                    return [dt, ...sortedStudents.map(s => {
                                        const rec = sess.attendance?.[s.id];
                                        if (!rec) return '-';
                                        const raw = rec.status || '';
                                        const status = raw === 'PRESENT'
                                            ? t('reports.common.present')
                                            : raw === 'ABSENT'
                                                ? t('reports.common.absent')
                                                : (raw || '-');
                                        if (!showCharges) return status || '-';
                                        return `${status} ${formatCurrency(rec.charge ?? 0)}`;
                                    })];
                                });
                                const totalsRow = showCharges
                                    ? [t('reports.common.totals'), ...sortedStudents.map(s => {
                                        const d = students[s.id];
                                        return `${t('reports.common.charges')}: ${formatCurrency(d?.totalCharges ?? 0)}`;
                                    })]
                                    : null;
                                const rows = totalsRow
                                    ? [headerRow, ...dataRows, totalsRow]
                                    : [headerRow, ...dataRows];
                                exportMatrixSheet(rows, 'session-attendance');
                            }}
                        />
                    </div>
                </div>
            </div>

            {loading && (
                <ListLoadingSpinner />
            )}

            {!loading && loadError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {loadError}
                </div>
            )}

            {!loading && report && sessions.length === 0 && (
                <div className="rounded-xl border border-slate-200 bg-white px-5 py-10 text-center shadow-sm">
                    <p className="font-medium text-slate-800">{t('common.noDataFound')}</p>
                    <p className="mt-1 text-sm text-slate-500">{t('reports.sessionAttendance.subtitleEnd')}</p>
                </div>
            )}

            {!loading && report && sessions.length > 0 && (
                <div className="overflow-x-auto bg-white rounded-xl shadow-md border border-gray-100">
                    <table className="report-matrix-table min-w-full border text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="border p-2 text-center">{t('reports.common.dateTime')}</th>
                                {sortedStudents.map((s) => (
                                    <th key={s.id} className="border p-2 text-center">
                                        <UserLink id={s.id} name={s.name} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sessionsPagination.pageItems.map((sess, idx) => (
                                <tr key={idx}>
                                    <td className="border p-2 align-top text-center">
                                        <SessionDateTimeDisplay
                                            date={sess.sessionDate}
                                            timeRange={sess.sessionTime}
                                        />
                                        {sess.sessionNotes && sess.sessionNotes !== "-" && (
                                            <div className="mt-2 p-2 rounded bg-blue-50 border-l-4 border-blue-400">
                                                <div className="text-xs font-semibold text-blue-700">📝 {t('reports.common.sessionNotes')}</div>
                                                <div className="text-xs text-gray-800 mt-1">{sess.sessionNotes}</div>
                                            </div>
                                        )}
                                    </td>
                                    {sortedStudents.map((s) => {
                                        const cell = sess.attendance[s.id];
                                        const status = cell?.status || "-";
                                        return (
                                            <td key={s.id} className="border p-1 align-middle">
                                                <div className="flex flex-col items-center justify-center text-center gap-1">
                                                {status === "PRESENT" ? (
                                                    <>
                                                        <div className="text-xs font-semibold text-green-700 bg-green-100 rounded inline-block px-0.5">{t('reports.common.present')}</div>
                                                        {showCharges && (
                                                            <div className="text-xs text-gray-600">{formatCurrency(cell?.charge || 0)}</div>
                                                        )}
                                                        {cell?.walkIn && <div className="text-xs font-semibold text-blue-700 bg-blue-100 rounded inline-block px-1">{t('reports.common.walkIn')}</div>}
                                                    </>
                                                ) : status === "ABSENT" ? (
                                                    <>
                                                        <div className="text-xs font-semibold text-red-700 bg-red-100 rounded inline-block px-0.5">{t('reports.common.absent')}</div>
                                                        {showCharges && (
                                                            <div className="text-xs text-gray-600">{formatCurrency(cell?.charge || 0)}</div>
                                                        )}
                                                        {cell?.walkIn && <div className="text-xs font-semibold text-blue-700 bg-blue-100 rounded inline-block px-1">{t('reports.common.walkIn')}</div>}
                                                    </>
                                                ) : (
                                                    <div className="text-gray-400">-</div>
                                                )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            {showCharges && (
                            <tr className="bg-gray-100 font-semibold">
                                <td className="border p-2">{t('reports.common.totalSessionCharges')}</td>
                                {sortedStudents.map((s) => (
                                    <td key={s.id} className="border p-2 text-center whitespace-nowrap">
                                        {formatCurrency(s.totalCharges || 0)}
                                    </td>
                                ))}
                            </tr>
                            )}
                        </tbody>
                    </table>
                    {sessionsPagination.total > 0 && (
                        <PaginationFooter
                            page={sessionsPagination.page}
                            size={sessionsPagination.size}
                            total={sessionsPagination.total}
                            onPageChange={sessionsPagination.setPage}
                            onSizeChange={sessionsPagination.setSize}
                        />
                    )}
                </div>
            )}

            {/* Export & Email */}
            <ReportExportBar
                disabled={!report}
                onExportPdf={onExportPdf}
                onSendEmail={async (emailAddr) => {
                    await emailSessionAttendanceReport({
                        role,
                        email: emailAddr,
                        fromDate: toIsoDate(fromDate),
                        toDate: toIsoDate(toDate),
                        batchId,
                    });
                }}
                emailLabel={t('reports.common.enterEmail')}
            />
        </div>
    );
};

export default SessionAttendanceReport;